// Main-thread client for the module-loader worker (see module-loader-worker.ts).
//
// `importAutomergePackageViaWorker` is wired into the ModuleWatcher in place of
// its default (direct, main-thread) package import. It asks the worker to
// import the package entry point and report which plugins it exports, then
// returns the same `{ plugins }` shape the watcher already feeds to
// `registerPlugins` — except each plugin's `load()` re-imports the package
// (pinned to the same heads) on this thread and runs the real plugin loader.
import { importPluginFromFolderDocUrl } from "@inkandswitch/patchwork-filesystem";
const WORKER_PATH = "/module-loader-worker.js";
let worker;
let nextRequestId = 1;
const pending = new Map();
function getWorker() {
    if (worker)
        return worker;
    worker = new Worker(WORKER_PATH, {
        type: "module",
        name: "patchwork-module-loader",
    });
    worker.addEventListener("message", (event) => {
        const data = event.data;
        if (!data || (data.type !== "descriptors" && data.type !== "error"))
            return;
        const entry = pending.get(data.id);
        if (!entry)
            return;
        pending.delete(data.id);
        if (data.type === "descriptors")
            entry.resolve(data.descriptors);
        else
            entry.reject(new Error(data.error));
    });
    worker.addEventListener("error", (event) => {
        // An uncaught worker error can't be tied to a single request — fail every
        // outstanding one so callers don't hang.
        const error = new Error(`module-loader worker error: ${event.message ?? "unknown"}`);
        for (const [, entry] of pending)
            entry.reject(error);
        pending.clear();
    });
    return worker;
}
/** Ask the worker which plugins the package at `urlAtHeads` exports. */
function discoverDescriptors(urlAtHeads) {
    const id = nextRequestId++;
    return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        getWorker().postMessage({ type: "discover", id, url: urlAtHeads });
    });
}
/**
 * ModuleWatcher `importAutomergePackage` hook: discover descriptors in the
 * worker, then return the `{ plugins }` shape with a main-thread `load()` per
 * plugin that imports the package at heads and calls its real loader.
 */
export async function importAutomergePackageViaWorker(urlAtHeads) {
    const url = urlAtHeads;
    const descriptors = await discoverDescriptors(url);
    const plugins = descriptors.map((descriptor) => {
        const { id, type } = descriptor;
        // A plugin id is only unique within a plugin type, so both are needed to
        // re-select the right plugin when its load() re-imports the package.
        if (typeof id !== "string" || typeof type !== "string")
            return descriptor;
        return {
            ...descriptor,
            load: () => importPluginFromFolderDocUrl(url, type, id),
        };
    });
    return { plugins };
}
